/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Autocomplete,
  AutocompleteChangeReason,
  Box,
  Chip,

  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { FormsIds, GenericSettings, HoursTypes } from "../../Utils/Constants";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  HoursValidationSchema,
  OrderModel,
  ProductsModel,
  VehicleModel,
} from "../../Utils/Models";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ro } from "date-fns/locale";
import StopIcon from "@mui/icons-material/Stop";
import { HoursFormProps } from "../../Utils/Types";
import { getVehicleLabel } from "../Vehicles/VehicleUtils";
import productStyles from "../../Styles/products.module.css";
import styles from "./HoursForm.module.css";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";
import { AxiosResponse } from "axios";
import { onFormError } from "../../Utils/Utils";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import moment from "moment";
import { useParams } from "react-router-dom";
import { NumericFormatCustom } from "../../Utils/NumericFormatComponents";
import { HourStatusEnum } from "../../Utils/Enums";

export default function HoursForm(props: HoursFormProps) {
  const {
    instructors,
    onClose,
    defaultValues,
    showKm,
    showVehicle = true,
    products = [],
    isInstructor = false,
  } = props;
  const [vehicles, setVehicles] = useState([] as VehicleModel[]);
  const ref: any = useRef();
  const { id } = useParams(); //order id

  // Date constraints for scheduling
  const yesterday = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  }, []);

  const today = useMemo(() => new Date(), []);

  // Check if we're in calendar mode (scheduling future hours)
  const isCalendarMode = products && products.length > 0;

  // Max date: allow future dates in calendar mode, otherwise restrict to today
  const maxDate = useMemo(() => {
    if (isCalendarMode) {
      // Allow scheduling up to 1 year in the future for calendar mode
      const futureDate = new Date();
      futureDate.setFullYear(futureDate.getFullYear() + 1);
      return futureDate;
    }
    return today;
  }, [isCalendarMode, today]);

  // Set instructor ID for instructors if not already set
  const getDefaultValues = () => {
    if (
      isInstructor &&
      !defaultValues?.instructorId &&
      instructors.length > 0
    ) {
      const userId = localStorage.getItem("userId");
      const currentInstructor: any = instructors.find(
        (inst: any) => inst._id === userId
      );
      if (currentInstructor?.instructorDetails?.[0]?._id) {
        return {
          ...defaultValues,
          instructorId: currentInstructor.instructorDetails[0]._id,
        };
      }
    }
    return defaultValues;
  };

  const { control, getValues, setValue, handleSubmit, watch, clearErrors } =
    useForm({
      resolver: yupResolver(HoursValidationSchema),
      defaultValues: {
        ...getDefaultValues(),
        isMultipleMode: false,
        numberOfSessions: 1,
        sessionDuration: GenericSettings.drivingHourInterval,
      },
      mode: "onChange",
      reValidateMode: "onChange",
      context: { isCalendarMode },
      shouldFocusError: true,
      shouldUnregister: false,
      criteriaMode: "firstError",
    });
  const startHour = watch("startHour");
  const isMultipleMode = watch("isMultipleMode");
  const numberOfSessions = watch("numberOfSessions");
  const sessionDuration = watch("sessionDuration");

  // Generate preview of sessions for multiple mode
  const sessionsPreview = useMemo(() => {
    if (!startHour || !isMultipleMode) return [];
    const sessions = [];
    let currentStart = new Date(startHour);
    for (let i = 0; i < (numberOfSessions || 1); i++) {
      const currentEnd = new Date(currentStart);
      currentEnd.setMinutes(currentEnd.getMinutes() + (sessionDuration || 90));
      sessions.push({
        start: currentStart.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" }),
        end: currentEnd.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" }),
      });
      currentStart = new Date(currentEnd);
    }
    return sessions;
  }, [startHour, isMultipleMode, numberOfSessions, sessionDuration]);

  const durationOptions = [60, 90, 100];

  /**
   * Load vehicles for instructor on mount and when isInstructor changes
   */
  useEffect(() => {
    let instructorId = getValues("instructorId");
    if (instructorId) {
      if (typeof instructorId !== "string") {
        instructorId = instructorId._id;
      }
      getData(
        `${endpoints.vehicle.main}/instructor/paginated?page=0&perPage=100000&id=${instructorId}`
      ).then((res: AxiosResponse) => {
        if (!res?.data) return;
        setVehicles(res.data.results);
        
      });
    }
  }, [isInstructor]);

  /**
   * Retrieves the instructor object that matches the current instructor_id value.
   *
   * This function searches through the list of instructors and finds the one
   * whose ID matches the current instructor_id value stored in the form state.
   *
   * @returns The instructor object that corresponds with the current instructor_id,
   *          or undefined if no match is found.
   */
  const getValueForAutocompleteInstructors = () => {
    const value = getValues("instructorId");

    const instructorAsUser = instructors.find((item: any) => {
      const instructorId = item.instructorDetails?.[0]?._id;
      const valueId = typeof value === "string" ? value : value?._id;
      return instructorId === valueId;
    }) as any;

    return instructorAsUser;
  };

  const selectInstructorAndGetItsVehicles = (
    e: any,
    product: any,
    reason: any,
    details: any
  ) => {
    setValue(`instructorId`, details.option.instructorDetails[0]._id);
    const instructorId = details.option.instructorDetails[0]._id;
    getData(
      `${endpoints.vehicle.main}/instructor/paginated?page=0&perPage=100000&id=${instructorId}`
    ).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setVehicles(res.data.results);
    });
  };

  /**
   * Retrieves the vehicle object that matches the current vehicleId value.
   * 
   * @returns The vehicle object that corresponds with the current vehicleId,
   *          or undefined if no match is found.
   */
  const getValueForAutocompleteVehicles = () => {
    const value = getValues("vehicleId");
    if (!value) return undefined;
    
    const vehicleId = typeof value === "string" ? value : value?._id;
    const vehicle = vehicles.find((v: VehicleModel) => v._id === vehicleId);
    
    return vehicle || undefined;
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    if (model._id) {
      updateElement(model);
    } else if (model.isMultipleMode && model.numberOfSessions > 1) {
      createMultipleElements(model);
    } else {
      createElement(model);
    }
  };

  const transformModelToDto = (model: any) => {
    const { date, startHour, endHour, productIds } = model;

    const formattedStartHour = moment(startHour).format("HH:mm");
    const formattedEndHour = moment(endHour).format("HH:mm");
    const formattedDate = moment(date).format("YYYY-MM-DD");

    model.startDate = `${formattedDate}T${formattedStartHour}:00`;
    model.endDate = `${formattedDate}T${formattedEndHour}:00`;
    model.orderId = id;
    model.type = HoursTypes.driving;
    model.productIds = productIds?.map((p: ProductsModel) => p._id) || [];

    if (showKm) {
      //it means the form is used for driving hours, not from calendar
      //so set the status to approved
      model.status = HourStatusEnum.APPROVED;
      model.clientId = defaultValues?.clientId;
    }

    return model;
  };
  /**
   * Create multiple hours based on numberOfSessions and sessionDuration
   */
  const createMultipleElements = async (model: any) => {
    const { date, startHour, numberOfSessions, sessionDuration, productIds } = model;
    const url = `${endpoints.hours.main}`;
    const formattedDate = moment(date).format("YYYY-MM-DD");

    try {
      let currentStart = new Date(startHour);

      for (let i = 0; i < numberOfSessions; i++) {
        const currentEnd = new Date(currentStart);
        currentEnd.setMinutes(currentEnd.getMinutes() + sessionDuration);

        const dto = {
          ...model,
          startDate: `${formattedDate}T${moment(currentStart).format("HH:mm")}:00`,
          endDate: `${formattedDate}T${moment(currentEnd).format("HH:mm")}:00`,
          orderId: id,
          type: HoursTypes.driving,
          productIds: productIds?.map((p: ProductsModel) => p._id) || [],
        };

        if (showKm) {
          dto.status = HourStatusEnum.APPROVED;
          dto.clientId = defaultValues?.clientId;
        }

        await postData(url, dto);
        currentStart = currentEnd;
      }
      onClose();
    } catch (error) {
      console.error("Error creating multiple hours:", error);
    }
  };

  /**
   * Create a new hour and add it to the database.
   * @param {object} model The new hour data.
   * @returns {Promise<void>}
   */
  const createElement = async (model: any) => {
    const url = `${endpoints.hours.main}`;

    const dto = transformModelToDto(model);

    try {
      const res = (await postData(url, dto)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      onClose();
    } catch (error) {
      console.error("Error creating hour:", error);
    }
  };

  /**
   * Update an existing hour in the database.
   * @param {object} model The hour data to update.
   * @returns {Promise<void>}
   */
  const updateElement = async (model: any) => {
    const url = `${endpoints.hours.main}/${model._id}`;

    const dto = transformModelToDto(model);

    try {
      const res = (await updateData(url, dto)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      onClose();
    } catch (error) {
      console.error("Error updating hour:", error);
    }
  };

  /**
   *
   */
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit(onSubmit, onFormError)(e);
      }}
      method="get"
      ref={ref}
      id={FormsIds.hours}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} sm={12} md={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <Controller
              control={control}
              name={`date`}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <DatePicker
                  label={Vocabulary.date}
                  value={field.value ? new Date(field.value) : null} // Convert string to Date
                  onChange={(newDate) => field.onChange(newDate)}
                  views={["month", "year", "day"]}
                  minDate={yesterday}
                  maxDate={maxDate}
                  slots={{ textField: TextField }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        {!defaultValues?._id && isCalendarMode && (
          <Grid item xs={12}>
            <Box className={styles.modeToggleContainer}>
              <EventRepeatIcon sx={{ color: "#1976d2" }} />
              <Typography className={styles.modeToggleLabel}>
                {Vocabulary.multipleIntervals}
              </Typography>
              <Controller
                control={control}
                name="isMultipleMode"
                render={({ field }) => (
                  <Switch
                    checked={field.value || false}
                    onChange={(e) => field.onChange(e.target.checked)}
                    color="primary"
                  />
                )}
              />
            </Box>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <Controller
              control={control}
              name={`startHour`}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TimePicker
                  label={Vocabulary.startHour}
                  value={field.value ? new Date(field.value) : null} // Convert string to Date
                  onChange={(newDate) => {
                    if (newDate) {
                      field.onChange(newDate);
                      // Add 90 minutes
                      const endDate = new Date(newDate);
                      endDate.setMinutes(
                        endDate.getMinutes() +
                          GenericSettings.drivingHourInterval
                      );
                      setValue("endHour", endDate);
                    }
                  }}
                  slots={{ textField: TextField }}
                  views={["hours", "minutes"]}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                    popper: {
                      sx: {
                        "& .MuiClockPicker-root": {
                          width: "100%",
                        },
                        "& .MuiMultiSectionDigitalClock-root": {
                          width: "100%",
                        },
                        "& .MuiList-root": {
                          width: "100%",
                        },
                        "& ul": {
                          width: "100%",
                        },
                      },
                      modifiers: [
                        {
                          name: "setWidth",
                          enabled: true,
                          phase: "beforeWrite",
                          requires: ["computeStyles"],
                          fn: ({ state }) => {
                            state.styles.popper.width = `${state.rects.reference.width}px`;
                          },
                        },
                      ],
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        {isMultipleMode ? (
          <Grid item xs={12}>
            <Box className={styles.multipleIntervalsContainer}>
              <Box className={styles.multipleIntervalsHeader}>
                <Box className={styles.multipleIntervalsIcon}>
                  <ScheduleIcon />
                </Box>
                <Box>
                  <Typography className={styles.multipleIntervalsTitle}>
                    {Vocabulary.sessionDuration}
                  </Typography>
                  <Typography className={styles.multipleIntervalsSubtitle}>
                    {Vocabulary.minutes}
                  </Typography>
                </Box>
              </Box>

              <Controller
                control={control}
                name="sessionDuration"
                render={({ field }) => (
                  <Box className={styles.durationChipsContainer}>
                    {durationOptions.map((duration) => (
                      <Box
                        key={duration}
                        className={`${styles.durationChip} ${
                          field.value === duration ? styles.durationChipActive : ""
                        }`}
                        onClick={() => field.onChange(duration)}
                      >
                        {duration} {Vocabulary.minutes}
                      </Box>
                    ))}
                  </Box>
                )}
              />

              <Box className={styles.sessionsCounterContainer}>
                <Typography className={styles.sessionsCounterLabel}>
                  {Vocabulary.numberOfSessions}:
                </Typography>
                <Controller
                  control={control}
                  name="numberOfSessions"
                  render={({ field }) => (
                    <Box className={styles.sessionsCounter}>
                      <button
                        type="button"
                        className={styles.counterButton}
                        onClick={() => field.onChange(Math.max(1, (field.value || 1) - 1))}
                        disabled={field.value <= 1}
                      >
                        −
                      </button>
                      <span className={styles.counterValue}>{field.value || 1}</span>
                      <button
                        type="button"
                        className={styles.counterButton}
                        onClick={() => field.onChange(Math.min(10, (field.value || 1) + 1))}
                        disabled={field.value >= 10}
                      >
                        +
                      </button>
                    </Box>
                  )}
                />
              </Box>

              {sessionsPreview.length > 0 && (
                <Box className={styles.previewContainer}>
                  <Typography className={styles.previewTitle}>
                    <ScheduleIcon sx={{ fontSize: 16 }} />
                    {Vocabulary.sessions}
                  </Typography>
                  <Box className={styles.previewList}>
                    {sessionsPreview.map((session, index) => (
                      <Box key={index} className={styles.previewItem}>
                        <span className={styles.previewItemNumber}>{index + 1}</span>
                        {session.start} - {session.end}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        ) : (
          <Grid item xs={12} sm={6} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
              <Controller
                control={control}
                name={`endHour`}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TimePicker
                    label={Vocabulary.endHour}
                    value={field.value ? new Date(field.value) : null}
                    onChange={(newDate) => field.onChange(newDate)}
                    slots={{ textField: TextField }}
                    views={["hours", "minutes"]}
                    minTime={new Date(startHour?.toString())}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                      popper: {
                        sx: {
                          "& .MuiClockPicker-root": {
                            width: "100%",
                          },
                          "& .MuiMultiSectionDigitalClock-root": {
                            width: "100%",
                          },
                          "& .MuiList-root": {
                            width: "100%",
                          },
                          "& ul": {
                            width: "100%",
                          },
                        },
                        modifiers: [
                          {
                            name: "setWidth",
                            enabled: true,
                            phase: "beforeWrite",
                            requires: ["computeStyles"],
                            fn: ({ state }) => {
                              state.styles.popper.width = `${state.rects.reference.width}px`;
                            },
                          },
                        ],
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        )}
        {showKm && (
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name={`startKm`}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  name="startKm"
                  id="startKm"
                  type="number"
                  label={Vocabulary.startKm}
                  fullWidth
                  onChange={(event) => field.onChange(event.target.value)}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                    endAdornment: (
                      <InputAdornment position="end">
                        <PlayCircleOutlineIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
        )}
        {showKm && (
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name={`endKm`}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  name="endKm"
                  id="endKm"
                  label={Vocabulary.endKm}
                  fullWidth
                  onChange={(event) => field.onChange(event.target.value)}
                  InputProps={{
                    inputComponent: NumericFormatCustom,
                    endAdornment: (
                      <InputAdornment position="end">
                        <StopIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  error={!!error}
                  helperText={error ? error.message : ""}
                />
              )}
            />
          </Grid>
        )}

        {!isInstructor && (
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name={`instructorId`}
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  size="small"
                  disableClearable
                  id={`instructorId`}
                  fullWidth={true}
                  freeSolo={false}
                  disablePortal={false}
                  noOptionsText={Vocabulary.noResultsFound}
                  isOptionEqualToValue={(option: any, value: any) => {
                    return option?._id === value?._id || option?._id === value;
                  }}
                  getOptionLabel={(option: any) =>
                    `${option?.firstName} ${option?.lastName}`
                  }
                  options={instructors || []}
                  value={getValueForAutocompleteInstructors() ?? null}
                  onChange={(
                    e: any,
                    product: OrderModel,
                    reason: AutocompleteChangeReason,
                    details?: any
                  ) => {
                    field.onChange(details?._id);
                    selectInstructorAndGetItsVehicles(
                      e,
                      product,
                      reason,
                      details
                    );
                    clearErrors("instructorId");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        rows: 4,
                        multiline: false,
                      }}
                      size="medium"
                      fullWidth
                      name={`instructorId`}
                      label={Vocabulary.instructor}
                      variant="outlined"
                      error={!!error}
                      helperText={error ? error.message : ""}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {showVehicle && (
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name={`vehicleId`}
              control={control}
              defaultValue={null}
              render={({ field: { ref, ...field }, fieldState: { error } }) => {
                return (
                  <>
                    <Autocomplete
                      size="small"
                      disableClearable
                      id={`vehicleId`}
                      fullWidth={true}
                      className={productStyles.editProductOrderOffer}
                      freeSolo={false}
                      disablePortal={false}
                      noOptionsText={Vocabulary.noResultsFound}
                      isOptionEqualToValue={(option: any, value: any) => {
                        if (typeof value === "string") {
                          return option._id === value;
                        } else {
                          return option?._id === value?._id;
                        }
                      }}
                      getOptionLabel={(option: any) =>
                        getVehicleLabel(option, vehicles)
                      }
                      options={vehicles || []}
                      value={getValueForAutocompleteVehicles()}
                      onChange={(
                        e: any,
                        vehicle: VehicleModel,
                        reason: AutocompleteChangeReason,
                        details?: any
                      ) => {
                        field.onChange(vehicle?._id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth={true}
                          name={`vehicleId`}
                          label={Vocabulary.vehicle}
                          variant="outlined"
                          size="medium"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </>
                );
              }}
            />
          </Grid>
        )}
        {products?.length != 0 && (
          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name={`productIds`}
              control={control}
              defaultValue={[]}
              render={({ field: { ref, ...field }, fieldState: { error } }) => {
                // Extract unique exam categories from products
                const examCategories = Array.from(
                  new Set(
                    products
                      .map((p: any) => p.examCategory)
                      .filter((cat: any) => cat)
                  )
                ).sort();

                // Get current selected category based on selected products
                const currentCategory =
                  field.value && field.value.length > 0
                    ? field.value[0]?.examCategory
                    : null;

                return (
                  <>
                    <Autocomplete
                      size="small"
                      id="examCategory"
                      fullWidth={true}
                      freeSolo={false}
                      disablePortal={false}
                      noOptionsText={Vocabulary.noResultsFound}
                      options={examCategories}
                      value={currentCategory || null}
                      onChange={(_e: any, newCategory: string | null) => {
                        if (newCategory) {
                          // Get all products with the selected exam category
                          const productsForCategory = products.filter(
                            (p: any) => p.examCategory === newCategory
                          );
                          field.onChange(productsForCategory);
                        } else {
                          field.onChange([]);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth={true}
                          name="examCategory"
                          label={Vocabulary.examCategory}
                          variant="outlined"
                          size="medium"
                          error={!!error}
                          helperText={error ? error.message : ""}
                        />
                      )}
                    />
                    {field.value && field.value.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {Vocabulary.products}:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {field.value.map((product: any) => (
                            <Chip
                              key={product._id}
                              label={product.name}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                );
              }}
            />
          </Grid>
        )}
      </Grid>
    </form>
  );
}
